'use client';

import Icon from '@/components/Icon';
import WaveDivider from '@/components/WaveDivider';
import { IHelpCenterArticleSection } from '@/interfaces';
import { getThemeBackgroundClass, getThemeColorValue } from '@/lib/theme';
import { ITheme } from '@/types/theme';
import Image from 'next/image';
import ContentListItems from './ContentListItems';
import FeatureStillMedia from './FeatureStillMedia';
import type { FeatureStill } from '@/types/feature-media';

interface AlternatingContentSectionsProps {
  content: IHelpCenterArticleSection[];
  language: 'en' | 'zh';
  theme: ITheme | null | undefined;
  backgroundColor?: 'primary' | 'secondary';
  media?: FeatureStill[];
}

// Helper function to parse HTML and extract list items for custom rendering
function parseContentWithLists(htmlContent: string): {
  beforeList: string;
  listItems: string[];
  afterList: string;
} {
  // Check if content contains <ul><li> structure
  const ulMatch = htmlContent.match(/<ul[^>]*>([\s\S]*?)<\/ul>/);

  if (!ulMatch) {
    return { beforeList: htmlContent, listItems: [], afterList: '' };
  }

  const beforeList = htmlContent.substring(0, ulMatch.index);
  const afterList = htmlContent.substring(
    (ulMatch.index || 0) + ulMatch[0].length
  );
  const listContent = ulMatch[1];

  // Extract individual <li> items
  const liRegex = /<li[^>]*>([\s\S]*?)<\/li>/g;
  const listItems: string[] = [];
  let match;

  while ((match = liRegex.exec(listContent)) !== null) {
    listItems.push(match[1].trim());
  }

  return { beforeList, listItems, afterList };
}

export default function AlternatingContentSections({
  content,
  language,
  theme,
  backgroundColor = 'primary',
  media,
}: AlternatingContentSectionsProps) {
  return (
    <>
      {content.map((section, index) => {
        const still = media?.find(asset =>
          asset.sectionKey
            ? asset.sectionKey ===
              section.help_center_article_section_extra_json?.media_key
            : asset.sectionId !== null &&
              asset.sectionId === section.help_center_article_section_id
        );
        // Alternate background color starting with the provided backgroundColor
        const currentBg =
          index % 2 === 0
            ? backgroundColor
            : backgroundColor === 'primary'
              ? 'secondary'
              : 'primary';

        const bgClasses = getThemeBackgroundClass(theme as ITheme, currentBg);

        // Parse content for list items
        const htmlContent =
          language === 'en'
            ? section.help_center_article_section_content_en
            : section.help_center_article_section_content_cn;

        const { beforeList, listItems, afterList } =
          parseContentWithLists(htmlContent);
        const imageAlt =
          language === 'en'
            ? section.help_center_article_section_extra_json?.image_alt_en
            : section.help_center_article_section_extra_json?.image_alt_cn;
        const sectionTitle =
          language === 'en'
            ? section.help_center_article_section_title_en
            : section.help_center_article_section_title_cn;

        // Convert list items to ContentListItems format with default icon
        const formattedListItems = listItems.map(item => ({
          text_en: item,
          text_cn: item,
          icon: 'custom-bullet-point' as const,
        }));

        return (
          <section
            key={index}
            id={section.help_center_article_section_extra_json?.anchor}
            className={`relative pt-16 pb-32 ${bgClasses}`}
          >
            <WaveDivider
              position="top"
              backgroundColor={currentBg}
              theme={theme}
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}
              >
                {/* Image or Icon */}
                <div
                  className={`flex justify-center ${index % 2 === 1 ? 'lg:col-start-2' : ''} h-full`}
                >
                  {still ? (
                    <FeatureStillMedia media={still} />
                  ) : section.help_center_article_section_extra_json?.icon ? (
                    <div className="relative flex w-full max-w-96 items-center justify-center p-4 sm:p-8">
                      <div
                        className="relative aspect-square w-full max-w-80 rounded-full shadow-2xl p-12 sm:p-16"
                        style={{
                          background: `linear-gradient(135deg, ${getThemeColorValue(theme as ITheme)}15 0%, ${getThemeColorValue(theme as ITheme)}30 100%)`,
                          border: `3px solid ${getThemeColorValue(theme as ITheme)}40`,
                        }}
                      >
                        <Icon
                          icon={
                            section.help_center_article_section_extra_json.icon
                          }
                          className="w-full h-full"
                          style={{ color: getThemeColorValue(theme as ITheme) }}
                        />
                      </div>
                    </div>
                  ) : section.help_center_article_section_extra_json?.image &&
                    section.help_center_article_section_extra_json.image.includes(
                      'circle=true'
                    ) ? (
                    <div className="relative flex w-full max-w-96 items-center justify-center p-4 sm:p-8">
                      <div
                        className="relative aspect-square w-full max-w-80 rounded-full shadow-2xl p-6"
                        style={{
                          background: `linear-gradient(135deg, ${getThemeColorValue(theme as ITheme)}15 0%, ${getThemeColorValue(theme as ITheme)}30 100%)`,
                          border: `3px solid ${getThemeColorValue(theme as ITheme)}40`,
                        }}
                      >
                        <div className="relative w-full h-full rounded-full overflow-hidden">
                          <Image
                            src={
                              section.help_center_article_section_extra_json
                                .image
                            }
                            alt={imageAlt || sectionTitle}
                            width={400}
                            height={400}
                            className="rounded-full object-cover w-full h-full"
                          />
                        </div>
                      </div>
                    </div>
                  ) : section.help_center_article_section_extra_json?.image ? (
                    <div
                      className={`${section.help_center_article_section_extra_json.image.includes('border=true') ? 'shadow-md border-1 border-gray-300 dark:border-gray-600 rounded-xl' : ''} max-w-full`}
                    >
                      <Image
                        src={
                          section.help_center_article_section_extra_json.image
                        }
                        alt={imageAlt || sectionTitle}
                        width={1200}
                        height={900}
                        style={{
                          width: 'auto',
                          height: '100%',
                          maxWidth: '100%',
                          maxHeight: '600px',
                        }}
                        className={`${section.help_center_article_section_extra_json.image.includes('?border=true') ? 'rounded-xl' : ''}`}
                      />
                    </div>
                  ) : null}
                </div>

                {/* Content */}
                <div
                  className={`${index % 2 === 1 ? 'lg:col-start-1' : ''} pt-6 pb-6`}
                >
                  <h2 className="text-3xl lg:text-4xl font-bold text-[var(--color-foreground)] mb-6">
                    {sectionTitle}
                  </h2>

                  {/* Content before list */}
                  {beforeList && (
                    <div
                      className="prose prose-lg max-w-none prose-headings:text-[var(--color-foreground)] prose-p:text-[var(--color-foreground-secondary)]"
                      style={{ color: 'var(--color-foreground-secondary)' }}
                      dangerouslySetInnerHTML={{ __html: beforeList }}
                    />
                  )}

                  {/* List items with custom bullet icons */}
                  {formattedListItems.length > 0 && (
                    <ContentListItems
                      items={formattedListItems}
                      language={language}
                      theme={theme}
                    />
                  )}

                  {/* Content after list */}
                  {afterList && (
                    <div
                      className="prose prose-lg max-w-none prose-headings:text-[var(--color-foreground)] prose-p:text-[var(--color-foreground-secondary)] mt-4"
                      style={{ color: 'var(--color-foreground-secondary)' }}
                      dangerouslySetInnerHTML={{ __html: afterList }}
                    />
                  )}
                </div>
              </div>
            </div>
            <WaveDivider
              position="bottom"
              backgroundColor={currentBg}
              theme={theme}
            />
          </section>
        );
      })}
    </>
  );
}
